import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  Channel,
  ChannelNameTokens,
  Destroyable,
  Rental,
  selectOnce,
  selectRentalById,
  isSomething,
  RatesChangedComplete,
  GroupRatesChangedComplete,
  selectSomeOnce,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { finalize, switchMap, take, tap, filter, debounceTime, map, startWith } from 'rxjs/operators'
import { selectAvailableRentalForNewAPIConnection } from '@tv3/store/connection/connection.selectors'
import { merge, Observable } from 'rxjs'
import { Connection } from '@tv3/store/connection/connection.model'
import {
  LinkConnectionWithRental,
  LinkConnectionWithRentalComplete,
  PushAvailabilityToConnectionComplete,
} from '@tv3/store/connection/connection.actions'
import { Actions, ofType } from '@ngrx/effects'
import { ConnectionHelperService } from '../../../connection-helper.service'
import { FormControl, Validators } from '@angular/forms'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'

@Component({
  selector: 'app-tiket-listing-wizard-step1',
  templateUrl: './tiket-listing-wizard-step1.component.html',
  styleUrls: ['./tiket-listing-wizard-step1.component.scss'],
})
export class TiketListingWizardStep1Component extends Destroyable implements OnInit {
  @Output() next = new EventEmitter()

  @Input() connection: Connection
  @Input() channel: Channel

  rentalIdCtrl = new FormControl('', [Validators.required])

  rentals$: Observable<Rental[]>

  isProcessing = false
  isLinked = false
  isAvailabilityPushed = false
  isRentalRatesReady = false
  constructor(
    private store: Store<any>,
    private connectionHelperService: ConnectionHelperService,
    private actions: Actions
  ) {
    super()
  }

  ngOnInit(): void {
    this.rentals$ = this.store.pipe(
      selectOnce(selectAvailableRentalForNewAPIConnection, {
        id: this.channel.id,
        selected: this.connection.rentalId,
      })
    )

    this.rentalIdCtrl.setValue(this.connection.rentalId)
    if (this.connection.rentalId) {
      this.rentalIdCtrl.disable()
      this.isLinked = true
    }

    const rentalIdChanges$ = this.rentalIdCtrl.valueChanges.pipe(startWith(this.rentalIdCtrl.value))
    merge(this.actions.pipe(ofType(RatesChangedComplete, GroupRatesChangedComplete)), rentalIdChanges$)
      .pipe(
        debounceTime(500),
        map(() => this.rentalIdCtrl.value),
        filter(isSomething),
        switchMap((rentalId) => this.store.pipe(selectSomeOnce(selectRentalById(rentalId)))),
        filter(isSomething),
        switchMap((rental) => this.connectionHelperService.checkBeforePushRates(rental, this.connection)),
        untilDestroy(this)
      )
      .subscribe((cats) => {
        this.isRentalRatesReady = isSomething(cats)
      })
  }

  onPushAvailability() {
    this.isProcessing = true
    this.connectionHelperService
      .pushAvailabilityForTiketLike(this.connection, ChannelNameTokens.Tiket)
      .pipe(
        switchMap(() => this.actions.pipe(ofType(PushAvailabilityToConnectionComplete))),
        take(1),
        tap(() => (this.isAvailabilityPushed = true)),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe()
  }

  onRates() {
    this.store.dispatch(OpenRentalOverlay({ rentalId: this.rentalIdCtrl.value, activeTab: RentalDialogTab.RatesTable }))
  }

  onLink() {
    this.isProcessing = true
    this.store.dispatch(
      LinkConnectionWithRental({
        connId: this.connection.id,
        channelName: ChannelNameTokens.Tiket,
        data: {
          rentalId: this.rentalIdCtrl.value,
          channelId: this.connection.channelId,
          propertyId: this.connection.propertyId,
          roomId: this.connection.roomId,
        },
      })
    )
    this.actions
      .pipe(
        ofType(LinkConnectionWithRentalComplete),
        take(1),
        tap(() => (this.isLinked = true)),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe()
  }
}
